import React, {useEffect, useState, createContext, useContext, useReducer} from "react";
import firebase from "firebase/app";
import * as Sentry from "@sentry/browser";
import Promise from "bluebird";
import Cookies from "js-cookie";

const UserContext = createContext({});

const baseUrl =
  process.env.NODE_ENV === "development" && process.env.PRIVATE_URL
    ? "http://localhost:8080"
    : "https://api.hackernoon.com/auth";

const POSTAUTH_FAILURE_MSG = "The cookie exchange failed, signing in anonymously...";

const initialState = {
  uid: null,
  email: null,
  name: null,
  handle: null,
  avatar: null,
  bio: null,
  twitter: null,
  facebook: null,
  linkedin: null,
  github: null,
  paymentPointer: null,
  preferredCharity: null,
  credits: null,
  isAdmin: null,
  isEditor: null,
  isSponsor: null,
  isAuthor: null,
  isBrand: null,
  isReader: null,
  isTrusted: null,
  secretLab: false,
  type: null,
  colors: null,
  onCart: 0,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "reset":
      return initialState;
    case "set":
    case "setUser": {
      return {...state, ...action.payload, _fbUser: firebase.auth().currentUser};
    }
    default:
      return state;
  }
};

const UserContextProvider = ({children}) => {
  let [user, userDispatch] = useReducer(reducer, initialState);
  const [loadingAuthState, setLoadingAuthState] = useState(true);
  const [isOnboarding, setIsOnboarding] = useState(false);

  const db = firebase.firestore();

  const setUserData = async user => {
    try {
      const userDoc = db.collection("users").doc(user.uid);
      const profileDoc = db.collection("profiles").doc(user.uid);
      const prefsDoc = db.collection("preferences").doc(user.uid);
      const statsDoc = db.collection("stats").doc(user.uid);

      const updateUser = doc => {
        userDispatch({type: "setUser", payload: doc.data()});
      };

      userDoc.onSnapshot(updateUser);
      profileDoc.onSnapshot(updateUser);
      statsDoc.onSnapshot(statsDoc);

      const [userRoles, profile, preferences, stats] = await Promise.all([
        userDoc.get(),
        profileDoc.get(),
        prefsDoc.get(),
        statsDoc.get(),
      ]);

      if (!profile.exists) {
        setIsOnboarding(true);
      }

      const roles = userRoles.data() || {};
      const profileData = profile.data();
      const preferencesData = preferences.exists ? preferences.data() : {};
      const {totalReadingTime, storyCount} = stats.exists ? stats.data() : {};
      const avatar = profileData?.avatar
        ? profileData.avatar?.replace("https://hackernoon.com", "https://cdn.hackernoon.com")
        : user.photoURL || `https://cdn.hackernoon.com/avatars/${user.uid}.png`;

      if (roles.optOut) {
        if (window.confirm("You have deactivated your HackerNoon account. Would you like to reactivate it?")) {
          const auth = firebase.auth();
          const token = await auth.currentUser.getIdToken();
          const url = "https://us-central1-hackernoon-app.cloudfunctions.net/app/account-activate";
          await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
        } else {
          await firebase.auth().signOut();
          userDispatch({type: "reset"});
          return;
        }
      }

      await Sentry.configureScope(scope => {
        scope.setUser({
          id: user.uid,
          email: user.email,
          handle: profileData?.handle,
        });
      });

      const mongoUrl = new URL("https://us-central1-hackernoon-app.cloudfunctions.net/hackernoonMongo2/services/cart");
      // const mongoUrl = new URL("http://localhost:8085/services/cart");
      mongoUrl.searchParams.append("recommended", "false");
      const mongoToken = await firebase.auth().currentUser.getIdToken();

      var onCart = 0;
      try {
        const req = await fetch(mongoUrl?.toString(), {
          method: "GET",
          headers: {"Content-Type": "application/json", Authorization: `Bearer ${mongoToken}`},
          // credentials: "include",
        });
        const response = await req.json();
        if (response.ok === true) {
          onCart = response?.cartOpions?.length || 0;
        }
      } catch (e) {
        console.log("something went wrong!", e);
      }

      userDispatch({
        type: "setUser",
        payload: {
          ...initialState,
          ...{
            uid: user.uid,
            email: user.email,
            ...roles,
            ...profileData,
            ...preferencesData,
            hasAboutPage: profileData?.about_page_settings ? true : false,
            avatar,
            storyCount,
            totalReadingTime,
            onCart,
          },
        },
      });
    } finally {
      setLoadingAuthState(false);
    }
  };

  const logout = async () => {
    await fetch(baseUrl + "/logout", {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    await firebase.auth().signOut();
    userDispatch({type: "reset"});
  };

  useEffect(() => {
    // a note on `user.isAnonymous === undefined`. I know this is weird,
    // but it's because we don't define it in the initial state, and this is the only case we want to target
    if (!loadingAuthState && user.uid && user.isAnonymous === undefined) {
      setIsOnboarding(!user.handle);
    }
  }, [user, loadingAuthState]);

  const tryPostAuth = async () => {
    try {
      const hasAuthCookie = Cookies.get("hasAuthCookie", {domain: ".hackernoon.com"});
      if (!hasAuthCookie) {
        return;
      }

      const postAuthRes = await fetch(baseUrl + "/postauth", {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!postAuthRes.ok) {
        throw new Error(POSTAUTH_FAILURE_MSG);
      }

      const {token} = await postAuthRes.json();

      if (!token) {
        throw new Error(POSTAUTH_FAILURE_MSG);
      }

      const {user} = await firebase.auth().signInWithCustomToken(token);
      if (!user) {
        throw new Error(POSTAUTH_FAILURE_MSG);
      }

      setUserData(user);
    } catch (err) {
      console.error(err);

      await firebase.auth().signInAnonymously();

      if (err.message === POSTAUTH_FAILURE_MSG) {
        userDispatch({type: "reset"});
      } else {
        throw err;
      }
    } finally {
      setLoadingAuthState(false);
    }
  };

  useEffect(() => {
    firebase.auth().onAuthStateChanged(async user => {
      if (user && !user.isAnonymous) {
        setUserData(user);
        const token = await user.getIdToken(true);

        await fetch(baseUrl + "/auth", {
          method: "POST",
          mode: "cors",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({idToken: token}),
        });
      } else if (!user) {
        await tryPostAuth();
      }
    });
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        authenticated: Boolean(user?._fbUser?.uid),
        getCurrentUser: () => firebase.auth().currentUser,
        userDispatch,
        loadingAuthState,
        isOnboarding,
        setIsOnboarding,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

let UserContextConsumer = UserContext.Consumer;

const useUser = () => useContext(UserContext);

const authenticate = async idToken => {
  await fetch(`${baseUrl}/auth`, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({idToken: idToken}),
  });
};

export {authenticate, UserContext, UserContextProvider, UserContextConsumer, useUser};
